import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Chart } from 'react-chartjs-2'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { DetailedGapDescription } from '../../shared/model/models'
import { DiversityDimenstionConfig, ModuleConfig } from '../../shared/model'
import { translatedDomainName } from '../../shared/utils'
import { fsColors } from '../../shared/constants'

type FsDescriptionByColumnPercentProps = {
  descriptionByColumnPercent: DetailedGapDescription
  moduleConfig?: ModuleConfig
  diversityDimenstionConfig?: DiversityDimenstionConfig
}
interface GroupData {
  referenceGroupName: string
  groupOfInterestName: string
  descriptiveName: string
  referenceGroupCount: number
  groupOfInterestCount: number
  color: string
}
interface ChartItem {
  value: string
  label: string
  name: string
  color: string
}

const FsDescriptionByColumnPercentage = (props: FsDescriptionByColumnPercentProps) => {
  const { descriptionByColumnPercent, moduleConfig, diversityDimenstionConfig } = props
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const [hideIndices, setHideIndices] = useState<number[]>([])
  const groupData: GroupData[] = []
  const groupOfInterestCount: number[] = []
  const groupOfInterest: ChartItem[] = []
  const referenceGroup: ChartItem[] = []
  const goiLabel = translatedDomainName(
    `${descriptionByColumnPercent.groupOfInterest.groupVariable.label}`,
    t,
  )
  const rgLabel = translatedDomainName(
    `${descriptionByColumnPercent.referenceGroup.groupVariable.label}`,
    t,
  )
  descriptionByColumnPercent.groupOfInterest.descriptions.forEach((groupOfInterestSet) => {
    groupOfInterestCount.push(groupOfInterestSet.descriptionDependentVariable.numberOfObservations)
  })

  const colors = [
    ...(diversityDimenstionConfig?.fsColor?.colors ?? []).slice(
      0,
      descriptionByColumnPercent.referenceGroup.descriptions.length < 5
        ? descriptionByColumnPercent.referenceGroup.descriptions.length - 1
        : 4,
    ),
    theme.palette.divider,
  ]

  descriptionByColumnPercent.referenceGroup.descriptions.forEach(() =>
    colors.push(fsColors.fsGray.colors[1]),
  )

  descriptionByColumnPercent.referenceGroup.descriptions.forEach((referenceGroupSet, index) => {
    groupData.push({
      referenceGroupName: rgLabel,
      groupOfInterestName: goiLabel,
      descriptiveName:
        translatedDomainName(`${referenceGroupSet.descriptiveVariable.label}`, t)
          .charAt(0)
          .toUpperCase() +
        translatedDomainName(`${referenceGroupSet.descriptiveVariable.label}`, t).slice(1),
      referenceGroupCount: referenceGroupSet.descriptionDependentVariable.numberOfObservations,
      groupOfInterestCount: groupOfInterestCount[index as number],
      color: colors[index],
    })
  })

  function getFormatedGroupValue(count: number) {
    return Number(
      (count * 100) /
        Math.round(groupData.reduce((prev, curr) => prev + +curr.groupOfInterestCount, 0)),
    ).toFixed(1)
  }

  function getFormatedGroupValueLabel(count: number) {
    return Number(
      (count * 100) /
        Math.round(groupData.reduce((prev, curr) => prev + +curr.groupOfInterestCount, 0)),
    ).toLocaleString(i18n.language, {
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    })
  }

  function getFormatedReferenceValue(count: number) {
    return Number(
      (count * 100) /
        Math.round(groupData.reduce((prev, curr) => prev + +curr.referenceGroupCount, 0)),
    ).toFixed(1)
  }

  function getFormatedReferenceValueLabel(count: number) {
    return Number(
      (count * 100) /
        Math.round(groupData.reduce((prev, curr) => prev + +curr.referenceGroupCount, 0)),
    ).toLocaleString(i18n.language, {
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    })
  }

  groupData
    .filter((_, iii) => !hideIndices.includes(iii))
    .forEach((groupDataItem) => {
      groupOfInterest.push({
        value: getFormatedGroupValue(groupDataItem.groupOfInterestCount),
        label: getFormatedGroupValueLabel(groupDataItem.groupOfInterestCount),
        name: t(`${groupDataItem.descriptiveName}` as any),
        color: groupDataItem.color,
      })
      referenceGroup.push({
        value: getFormatedReferenceValue(groupDataItem.referenceGroupCount),
        label: getFormatedReferenceValueLabel(groupDataItem.referenceGroupCount),
        name: t(`${groupDataItem.descriptiveName}` as any),
        color: groupDataItem.color,
      })
    })

  const helpTextHtml =
    descriptionByColumnPercent &&
    descriptionByColumnPercent.gapCauseDescriptions.filter((info) => info.locale === i18n.language)
      ? descriptionByColumnPercent.gapCauseDescriptions.filter(
          (info) => info.locale === i18n.language,
        )[0].gapEmpiricalPercentage
      : ''

  return (
    <div
      style={{
        height: '100%',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          color: '#666',
          fontWeight: 'bold',
          textAlign: 'center',
        }}
      >
        {t(
          `descriptiveCharacteristic.name.${descriptionByColumnPercent?.groupOfInterest.descriptiveCharacteristic.name}` as any,
        )}
      </Typography>
      <Stack
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        mb={2}
        flexWrap="wrap"
        sx={{ position: 'relative', zIndex: 100 }}
      >
        {groupData.map((g, index) => (
          <Stack
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            key={g.descriptiveName}
            sx={{ cursor: 'pointer' }}
            onClick={() =>
              hideIndices.includes(index)
                ? setHideIndices(hideIndices.filter((ii) => ii !== index))
                : setHideIndices([...hideIndices, index])
            }
            mt={1}
          >
            <Box height={13} width={36} sx={{ backgroundColor: colors[index] }} mr={1} ml={3} />
            <Typography
              sx={{
                fontSize: 14,
                color: '#666',
                textDecoration: hideIndices.includes(index) ? 'line-through' : '',
              }}
            >
              {t(`${g.descriptiveName}` as any)}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Box
        height={{ sm: 900, md: 450 }}
        display="flex"
        alignItems="center"
        justifyContent="space-around"
        flexDirection={{ sm: 'column', md: 'row' }}
        maxWidth={1000}
        mx="auto"
      >
        <Box
          width={{ sm: '100%', md: '50%' }}
          display="flex"
          justifyContent="center"
          maxWidth={450}
          height={450}
          minWidth={200}
        >
          <Chart
            type="pie"
            id={`description_percentage_goi_pie_chart_${moduleConfig?.module}`}
            data={{
              labels: groupOfInterest.map((g) => g.name),
              datasets: [
                {
                  label: t(`${groupData[0].groupOfInterestName}` as any),
                  backgroundColor: groupOfInterest.map((g) => g.color),
                  data: groupOfInterest.map((g) => g.value),
                },
              ],
            }}
            options={{
              maintainAspectRatio: false,
              locale: i18n.language,
              elements: { arc: { borderWidth: 0 } },
              layout: {
                padding: {
                  top: 20,
                  bottom: 10,
                  right: 20,
                },
              },
              plugins: {
                title: {
                  display: true,
                  text: t(`${groupData[0].groupOfInterestName}` as any),
                  color: theme.palette.text.secondary,
                  position: 'bottom',
                  padding: 30,
                },
                legend: {
                  display: false,
                },
                tooltip: {
                  callbacks: {
                    label: (context) => `${context.dataset.label}: ${context.formattedValue} %`,
                  },
                },
                datalabels: {
                  anchor: 'end',
                  align: 'end',
                  display: 'auto',
                  color: theme.palette.text.secondary,
                  formatter: (_, context) =>
                    groupOfInterest[context.dataIndex].value !== '0.0'
                      ? `${groupOfInterest[context.dataIndex].label} %`
                      : '',
                },
              },
              scales: {
                x: {
                  grid: {
                    display: false,
                  },
                  border: {
                    display: false,
                  },
                  ticks: {
                    display: false,
                  },
                },
                y: {
                  grid: {
                    display: false,
                  },
                  border: {
                    display: false,
                  },
                  ticks: {
                    display: false,
                  },
                },
              },
            }}
          />
        </Box>
        <Box
          width={{ sm: '100%', md: '50%' }}
          display="flex"
          justifyContent="center"
          maxWidth={450}
          height={450}
          minWidth={200}
        >
          <Chart
            type="pie"
            id={`description_percentage_rg_pie_chart_${moduleConfig?.module}`}
            data={{
              labels: referenceGroup.map((r) => r.name),
              datasets: [
                {
                  label: t(`${groupData[0].referenceGroupName}` as any),
                  backgroundColor: groupOfInterest.map((g) => g.color),
                  data: referenceGroup.map((r) => r.value),
                },
              ],
            }}
            options={{
              maintainAspectRatio: false,
              locale: i18n.language,
              elements: { arc: { borderWidth: 0 } },
              layout: {
                padding: {
                  top: 20,
                  bottom: 10,
                  left: 20,
                },
              },
              plugins: {
                title: {
                  display: true,
                  text: t(`${groupData[0].referenceGroupName}` as any),
                  color: theme.palette.text.secondary,
                  position: 'bottom',
                  padding: 30,
                },
                legend: {
                  display: false,
                },
                tooltip: {
                  callbacks: {
                    label: (context) => `${context.dataset.label}: ${context.formattedValue} %`,
                  },
                },
                datalabels: {
                  anchor: 'end',
                  align: 'end',
                  display: 'auto',
                  color: theme.palette.text.secondary,
                  formatter: (_, context) =>
                    referenceGroup[context.dataIndex].value !== '0.0'
                      ? `${referenceGroup[context.dataIndex].label} %`
                      : '',
                },
              },
              scales: {
                x: {
                  grid: {
                    display: false,
                  },
                  border: {
                    display: false,
                  },
                  ticks: {
                    display: false,
                  },
                },
                y: {
                  grid: {
                    display: false,
                  },
                  border: {
                    display: false,
                  },
                  ticks: {
                    display: false,
                  },
                },
              },
            }}
          />
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: 14,
          color: '#666',
          fontWeight: 'bold',
          textAlign: 'center',
        }}
        mt={-2}
        mb={2}
      >
        {t(
          `context.model.name.${
            descriptionByColumnPercent?.groupOfInterest.groupVariable.name.split('@')[0]
          }` as any,
        )}
      </Typography>
      <Box>
        <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
          {helpTextHtml}
        </Typography>
      </Box>
    </div>
  )
}

export default FsDescriptionByColumnPercentage
